/**
 * Admin: Master Accounts (MASTER_ACCOUNTS_PLAN.md, phase 2). The dealer's screen: masters, a trade
 * ticket and positions on the selected master, each position's per-follower results, the followers
 * (assign, multiplier, stop loss, funds, resync, unassign), mandates and the master's copy health.
 */
import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { COPY_LIMITS, COPY_SKIP_REASONS, D, fmt, fmtDateTime, fmtPct, fmtUsd, fmtUsdSigned, MASTER_LIMITS, type CopySummary, type MandateSummary, type MasterFeeStatement, type MasterSummary, type Order, type Position } from '@platform/shared';
import { ClientCell, clientLabel } from '../components/ClientCell';
import { InstrumentLabel } from '../components/InstrumentIcon';
import { cx, fmtTradeId, Modal, sideLabel, Spinner, Tabs, TradeId } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';
import { MasterTrading } from './AdminDeskAccounts';

const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-9 rounded-lg bg-accent px-4 text-xs font-semibold text-white disabled:opacity-50';

/** The client behind a follower or mandate: name (profile, else CRM) and CRM id, for the Client column. */
type ClientUser = { id: string; email: string; username: string; name: string | null; externalClientId: string | null };
type Follower = CopySummary & { user: ClientUser };
type Mandate = MandateSummary & { userName: string | null; userExternalClientId: string | null };
const userCell = (u: ClientUser) => <ClientCell name={u.name} email={u.email} crmId={u.externalClientId} />;
interface FollowerResult {
  copyId: string;
  user: ClientUser;
  action: 'open' | 'close' | 'tpsl';
  status: 'pending' | 'done' | 'skipped' | 'failed';
  reason: string | null;
  notional: string | null;
  /** Cut to the instrument's max order: the follower's share before the cut. */
  cappedFrom: string | null;
  ratio: string | null;
  entryPrice: string | null;
  positionStatus: string | null;
  slippage: string | null;
  signalAt: string | null;
  doneAt: string | null;
}
interface Health {
  pending: number;
  retrying: number;
  done_24h: number;
  skipped_24h: number;
  /** Copies cut to the instrument's max order. */
  capped_24h: number;
  p95_ms: number | null;
  skips: { reason: string; n: number }[];
}

/** Fee rates are fractions in the API ('0.02') and percents on screen ('2'). */
const toFrac = (pctText: string) => D.from(pctText.trim() || '0').div(100).toString();
const toPctText = (frac: string) => D.from(frac).mul(100).toString();
const feeText = (mgmt: string, perf: string) => (D.from(mgmt).isZero() && D.from(perf).isZero() ? 'none' : `${fmt(D.from(mgmt).mul(100), 2)}% / ${fmt(D.from(perf).mul(100), 0)}%`);

/** An unsigned percentage (fmtPct is signed, for changes). */
const pct = (x: D, dp = 2) => `${fmt(x, dp)}%`;
const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

/** Run an action with a busy flag and an error message; returns true when it worked. */
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(msgOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, run, setError };
}

function Err({ msg }: { msg: string | null }) {
  return msg ? (
    <p className="m-0 text-xs text-down" role="alert">
      {msg}
    </p>
  ) : null;
}

function Table({ head, children, minWidth = 760 }: { head: string[]; children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-panel">
      <table className="w-full border-collapse text-xs" style={{ minWidth }}>
        <thead className="border-b border-line">
          <tr>
            {head.map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'up' | 'down' | 'bad' }) {
  return (
    <div className={cx('rounded-xl border bg-panel p-3', tone === 'bad' ? 'border-down' : 'border-line')}>
      <div className="text-[11px] text-muted">{label}</div>
      <div className={cx('num mt-1 text-[16px] font-semibold', tone === 'up' && 'text-up', tone === 'down' && 'text-down')}>{value}</div>
    </div>
  );
}

/** A master's instrument caps, each with its icon. */
const InstrumentList = ({ symbols }: { symbols: string[] }) => (
  <>
    {symbols.map((s, i) => (
      <Fragment key={s}>
        {i > 0 && ', '}
        <InstrumentLabel symbol={s} size={14} className="gap-1" />
      </Fragment>
    ))}
  </>
);

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cx('flex flex-col gap-1 text-[11px] text-muted', className)}>
      {label}
      {children}
    </label>
  );
}

const signTone = (v: string) => (v.startsWith('-') ? 'down' : D.from(v).isZero() ? undefined : 'up');
const statusPill = (s: string) => (
  <span className={cx('rounded-full px-2 py-0.5 text-[11px] font-semibold', s === 'active' ? 'bg-up/15 text-up' : s === 'paused' ? 'bg-warn/15 text-warn' : 'bg-raised text-muted')}>{s}</span>
);

// ---------- tab ----------

export function MastersTab() {
  const [showClosed, setShowClosed] = useState(false);
  const { data, error, reload } = useLoad<{ masters: MasterSummary[] }>(`/api/admin/masters${showClosed ? '?all=1' : ''}`, [showClosed]);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const masters = data?.masters ?? null;
  const current = masters?.find((m) => m.id === selected) ?? null;

  useEffect(() => {
    if (!selected && masters?.length) setSelected(masters[0]!.id);
  }, [masters, selected]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-[13px] text-muted">Dealer-run accounts. Every trade on a master is copied to its followers, sized to their equity.</p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed
          </label>
          <button type="button" className={btn} onClick={() => setCreating(true)}>
            New master
          </button>
        </div>
      </div>
      <Err msg={error} />
      {!masters && !error && <Spinner />}
      {masters && !masters.length && <p className="m-0 text-sm text-muted">No master accounts yet.</p>}
      {!!masters?.length && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Master accounts">
          {masters.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={m.id === selected}
              onClick={() => setSelected(m.id)}
              className={cx('flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left', m.id === selected ? 'border-fg bg-raised' : 'border-line bg-panel hover:border-line-strong')}
            >
              <span className="flex items-center gap-2 text-[13px] font-semibold">
                {m.name} {statusPill(m.status)}
              </span>
              <span className="num text-[11px] text-muted">
                {fmtUsd(m.equity)} · <span className={m.pnl.startsWith('-') ? 'text-down' : 'text-up'}>{fmtUsdSigned(m.pnl)}</span> · {m.followers} follower{m.followers === 1 ? '' : 's'}
              </span>
            </button>
          ))}
        </div>
      )}
      {current && <MasterDetail key={current.id} master={current} onChanged={() => void reload()} />}
      <CreateMasterModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(m) => {
          setCreating(false);
          setSelected(m.id);
          void reload();
        }}
      />
    </div>
  );
}

function CreateMasterModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (m: MasterSummary) => void }) {
  const [name, setName] = useState('');
  const [ref, setRef] = useState('100000');
  const [lev, setLev] = useState('');
  const [maxOrder, setMaxOrder] = useState('');
  const [instruments, setInstruments] = useState('');
  const [note, setNote] = useState('');
  const [mgmtFee, setMgmtFee] = useState('0');
  const [perfFee, setPerfFee] = useState('0');
  const a = useAction();
  const submit = () =>
    a.run(async () => {
      const r = await api.post<{ master: MasterSummary }>('/api/admin/masters', {
        name,
        referenceEquity: ref,
        maxLeverage: lev || null,
        maxOrder: maxOrder || null,
        mgmtFeePct: toFrac(mgmtFee),
        perfFeePct: toFrac(perfFee),
        instruments: instruments
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        note,
      });
      setName('');
      setNote('');
      onCreated(r.master);
    });
  return (
    <Modal open={open} onClose={onClose} title="New master account" width={460}>
      <Field label="Name (clients see it)">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Gold Desk" />
      </Field>
      <Field label="Reference equity (USDT, paper money; followers are sized against the master's equity)">
        <input className={input} inputMode="decimal" value={ref} onChange={(e) => setRef(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Max leverage (optional)">
          <input className={input} inputMode="numeric" value={lev} onChange={(e) => setLev(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="Instruments (optional, comma separated)">
          <input className={input} value={instruments} onChange={(e) => setInstruments(e.target.value.toUpperCase())} placeholder="All" />
        </Field>
      </div>
      <MaxOrderField value={maxOrder} onChange={setMaxOrder} />
      <FeeFields mgmt={mgmtFee} perf={perfFee} onMgmt={setMgmtFee} onPerf={setPerfFee} />
      <Field label="Note (internal)">
        <textarea className={cx(input, 'h-20 py-2')} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <Err msg={a.error} />
      <button type="button" className={primary} disabled={a.busy || name.trim().length < 2 || !ref} onClick={() => void submit()}>
        {a.busy ? 'Creating…' : 'Create master'}
      </button>
    </Modal>
  );
}

// ---------- one master ----------

type Section = 'trade' | 'followers' | 'mandates' | 'settings';

function MasterDetail({ master: m, onChanged }: { master: MasterSummary; onChanged: () => void }) {
  const [section, setSection] = useState<Section>('trade');
  const health = useLoad<Health>(`/api/admin/masters/${m.id}/health`);
  const [closing, setClosing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const a = useAction();
  const closed = m.status === 'closed';
  const setStatus = (status: 'active' | 'paused') => a.run(() => api.patch(`/api/admin/masters/${m.id}`, { status })).then((ok) => ok && onChanged());
  const h = health.data;

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-line p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="m-0 flex items-center gap-2 text-lg font-semibold">
            {m.name} {statusPill(m.status)}
          </h2>
          <p className="m-0 mt-1 text-xs text-muted">
            Created {fmtDateTime(m.createdAt)} by {m.createdBy}
            {m.maxLeverage ? ` · max leverage ${m.maxLeverage}x` : ''}
            {m.instruments.length ? (
              <>
                {' · '}
                <InstrumentList symbols={m.instruments} />
              </>
            ) : (
              ' · all instruments'
            )}
          </p>
        </div>
        {!closed && (
          <div className="flex gap-2">
            <button type="button" className={btn} onClick={() => setRenaming(true)}>
              Rename
            </button>
            {m.status === 'active' ? (
              <button type="button" className={btn} disabled={a.busy} onClick={() => void setStatus('paused')} title="Followers skip new opens while paused; closes are still copied">
                Pause
              </button>
            ) : (
              <button type="button" className={btn} disabled={a.busy} onClick={() => void setStatus('active')}>
                Resume
              </button>
            )}
            <button type="button" className={cx(btn, 'border-down text-down')} onClick={() => setClosing(true)}>
              Close master
            </button>
          </div>
        )}
      </div>
      <Err msg={a.error} />
      {m.status === 'paused' && <p className="m-0 rounded-lg bg-warn/10 px-3 py-2 text-xs text-warn">Paused: your new opens are not copied to followers. Closes and TP/SL changes still are. Use Resync on a follower to catch up.</p>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="P&L since start" value={fmtUsdSigned(m.pnl)} tone={signTone(m.pnl)} />
        <Stat label="Followers · invested" value={`${m.followers} · ${fmtUsd(m.followersInvested, 0)}`} />
        <Stat label="Copied · 24h" value={h?.done_24h ?? '—'} />
        <Stat label="Skipped · capped · 24h" value={h ? `${h.skipped_24h} · ${h.capped_24h ?? 0}` : '—'} />
        <Stat label="Pending · p95" value={h ? `${h.pending}${h.retrying ? ` (${h.retrying} retrying)` : ''} · ${h.p95_ms != null ? `${h.p95_ms} ms` : '—'}` : '—'} tone={h && (h.retrying > 0 || (h.p95_ms ?? 0) > 1000) ? 'bad' : undefined} />
      </div>
      {!!h?.skips.length && <p className="m-0 text-xs text-muted">Skips (24h): {h.skips.map((s) => `${COPY_SKIP_REASONS[s.reason] ?? s.reason} ${s.n}`).join(' · ')}</p>}

      <Tabs<Section>
        size="sm"
        value={section}
        onChange={setSection}
        tabs={[
          { id: 'trade', label: 'Trade' },
          { id: 'followers', label: `Followers (${m.followers})` },
          { id: 'mandates', label: 'Mandates' },
          { id: 'settings', label: 'Settings' },
        ]}
      />
      {section === 'trade' && (
        <TradeSection
          master={m}
          onTraded={() => {
            onChanged();
            void health.reload();
          }}
        />
      )}
      {section === 'followers' && <FollowersSection master={m} onChanged={onChanged} />}
      {section === 'mandates' && <MandatesSection master={m} />}
      {/* Keyed by name so a rename from the header doesn't leave the old name in the form to be saved back. */}
      {section === 'settings' && <SettingsSection key={m.name} master={m} onChanged={onChanged} />}

      <CloseMasterModal open={closing} master={m} onClose={() => setClosing(false)} onDone={onChanged} />
      {renaming && <RenameMasterModal master={m} onClose={() => setRenaming(false)} onDone={onChanged} />}
    </div>
  );
}

/** Renames everywhere at once: clients, statements and the CRM read the master's name live. */
function RenameMasterModal({ master: m, onClose, onDone }: { master: MasterSummary; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(m.name);
  const a = useAction();
  const next = name.trim();
  const save = () =>
    a.run(() => api.patch(`/api/admin/masters/${m.id}`, { name: next })).then((ok) => {
      if (ok) {
        onClose();
        onDone();
      }
    });
  return (
    <Modal open onClose={onClose} title={`Rename ${m.name}`} width={440}>
      <Field label="New name (clients see it)">
        <input
          className={input}
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && next.length >= 2 && next !== m.name && !a.busy && void save()}
        />
      </Field>
      <p className="m-0 text-xs text-muted">2–60 characters. Followers, mandates and fee statements show the new name straight away. Past ledger entries keep the name they were written with.</p>
      <Err msg={a.error} />
      <button type="button" className={cx(primary, 'self-start')} disabled={a.busy || next.length < 2 || next === m.name} onClick={() => void save()}>
        {a.busy ? 'Saving…' : 'Rename'}
      </button>
    </Modal>
  );
}

function CloseMasterModal({ open, master: m, onClose, onDone }: { open: boolean; master: MasterSummary; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const a = useAction();
  return (
    <Modal open={open} onClose={onClose} title={`Close ${m.name}?`} width={440}>
      <p className="m-0 text-[13px]">
        This stops all {m.followers} follower{m.followers === 1 ? '' : 's'}: their positions close and their cash goes back to their live accounts. The master's own positions and orders close too.
        It can't be undone.
      </p>
      <Field label="Reason (kept in the audit)">
        <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Err msg={a.error} />
      <button
        type="button"
        className={cx(primary, 'bg-down')}
        disabled={a.busy || !reason.trim()}
        onClick={() =>
          void a.run(() => api.post(`/api/admin/masters/${m.id}/close`, { reason })).then((ok) => {
            if (ok) {
              onClose();
              onDone();
            }
          })
        }
      >
        {a.busy ? 'Closing…' : 'Close master'}
      </button>
    </Modal>
  );
}

// ---------- trade ----------

function TradeSection({ master: m, onTraded }: { master: MasterSummary; onTraded: () => void }) {
  const [drill, setDrill] = useState<{ id: string; tradeId: number | null; side: 'long' | 'short'; symbol: string; entryPrice: string } | null>(null);
  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-xs text-muted">
        Caps on this master: {m.maxLeverage ? `leverage up to 1:${m.maxLeverage}` : 'any leverage'} · {m.instruments.length ? <InstrumentList symbols={m.instruments} /> : 'all instruments'} · {m.maxOrder ? `orders up to ${fmtUsd(m.maxOrder, 0)}` : 'no max order beyond its margin'}. Each follower opens the same share of their own equity (× their multiplier), at most the instrument’s client max order; trades under the minimum are skipped.
      </p>
      <MasterTrading
        accountId={m.accountId}
        onChanged={onTraded}
        rowExtra={(p) => (
          <button type="button" className={btn} onClick={() => setDrill({ id: p.id, tradeId: p.tradeId, side: p.side, symbol: p.symbol, entryPrice: p.entryPrice })}>
            Followers
          </button>
        )}
      />
      <FollowerResultsModal master={m} position={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

function FollowerResultsModal({ master: m, position: p, onClose }: { master: MasterSummary; position: { id: string; tradeId: number | null; side: 'long' | 'short'; symbol: string; entryPrice: string } | null; onClose: () => void }) {
  const { data, error } = useLoad<{ followers: FollowerResult[] }>(p ? `/api/admin/masters/${m.id}/positions/${p.id}/followers` : null, [p?.id]);
  const rows = data?.followers ?? [];
  const opens = rows.filter((r) => r.action === 'open');
  const later = rows.filter((r) => r.action !== 'open');
  return (
    <Modal
      open={!!p}
      onClose={onClose}
      title={p ? `${p.tradeId != null ? `${fmtTradeId(p.tradeId)} · ` : ''}${sideLabel(p.side)} ${p.symbol} @ ${p.entryPrice}: followers` : 'Followers'}
      heading={
        p ? (
          <>
            {p.tradeId != null ? `${fmtTradeId(p.tradeId)} · ` : ''}
            {sideLabel(p.side)} <InstrumentLabel symbol={p.symbol} size={20} /> @ {p.entryPrice}: followers
          </>
        ) : undefined
      }
      width={820}
    >
      <Err msg={error} />
      {!data && !error && <Spinner />}
      {data && !rows.length && <p className="m-0 text-sm text-muted">No followers were running when this position opened.</p>}
      {!!opens.length && (
        <Table head={['Client', 'Open', 'Size', 'Ratio', 'Entry', 'Slippage', 'Now']} minWidth={680}>
          {opens.map((r) => (
            <tr key={`${r.copyId}-o`} className="border-b border-line">
              <td className={td}>{userCell(r.user)}</td>
              <td className={cx(td, r.status === 'done' ? 'text-up' : r.status === 'skipped' ? 'text-down' : 'text-muted')}>
                {r.status}
                {r.reason && r.status !== 'done' ? `: ${COPY_SKIP_REASONS[r.reason] ?? r.reason}` : ''}
                {r.cappedFrom && <span className="text-warn"> · capped: share {fmtUsd(r.cappedFrom, 0)}</span>}
              </td>
              <td className={cx(td, 'num')}>{r.notional ? fmtUsd(r.notional) : '—'}</td>
              <td className={cx(td, 'num')}>{r.ratio ? pct(D.from(r.ratio).mul(100), 3) : '—'}</td>
              <td className={cx(td, 'num')}>{r.entryPrice ?? '—'}</td>
              <td className={cx(td, 'num', r.slippage && r.slippage.startsWith('-') ? 'text-up' : r.slippage && !D.from(r.slippage).isZero() ? 'text-down' : '')}>
                {r.slippage ? fmtPct(D.from(r.slippage).mul(100), 3) : '—'}
              </td>
              <td className={td}>{r.positionStatus ?? '—'}</td>
            </tr>
          ))}
        </Table>
      )}
      {!!later.length && (
        <>
          <h3 className="m-0 text-sm font-semibold">Closes and TP/SL changes</h3>
          <Table head={['Client', 'Action', 'Status', 'When']} minWidth={560}>
            {later.map((r, i) => (
              <tr key={`${r.copyId}-${i}`} className="border-b border-line">
                <td className={td}>{userCell(r.user)}</td>
                <td className={td}>{r.action === 'tpsl' ? 'TP/SL' : 'Close'}</td>
                <td className={cx(td, r.status === 'done' ? 'text-up' : r.status === 'pending' ? 'text-warn' : 'text-down')}>
                  {r.status}
                  {/* A done close keeps a note when it says how (closed_live: from chart, at today's price). */}
                  {r.reason && (r.status !== 'done' || r.reason === 'closed_live') ? `: ${COPY_SKIP_REASONS[r.reason] ?? r.reason}` : ''}
                </td>
                <td className={cx(td, 'text-muted')}>{fmtDateTime(r.doneAt ?? r.signalAt)}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
      <p className="m-0 text-xs text-muted">Slippage is against the master's entry; positive means a worse price for the client.</p>
    </Modal>
  );
}

// ---------- followers ----------

function FollowersSection({ master: m, onChanged }: { master: MasterSummary; onChanged: () => void }) {
  const [all, setAll] = useState(false);
  const { data, error, reload } = useLoad<{ followers: Follower[] }>(`/api/admin/masters/${m.id}/followers${all ? '?all=1' : ''}`, [all]);
  const [assigning, setAssigning] = useState(false);
  const [edit, setEdit] = useState<Follower | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const refresh = () => {
    void reload();
    onChanged();
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Include stopped
        </label>
        {m.status === 'active' && (
          <button type="button" className={btn} onClick={() => setAssigning(true)}>
            Assign client
          </button>
        )}
      </div>
      {note && <p className="m-0 rounded-lg bg-up/10 px-3 py-2 text-xs text-up">{note}</p>}
      <Err msg={error} />
      {!data && !error && <Spinner />}
      {data && !data.followers.length && <p className="m-0 text-sm text-muted">No followers. Clients need an accepted mandate (Mandates tab) before you can assign them.</p>}
      {!!data?.followers.length && (
        <Table head={['Client', 'Status', 'Invested', 'Equity', 'P&L', 'Multiplier', 'Stop loss', 'Fees (mgmt / perf)', 'Open', 'Since', '']}>
          {data.followers.map((f) => (
            <tr key={f.id} className="border-b border-line">
              <td className={td}>{userCell(f.user)}</td>
              <td className={td}>
                {statusPill(f.status)}
                {f.stopReason ? <span className="ml-1 text-[11px] text-muted">{f.stopReason.replace('_', ' ')}</span> : null}
              </td>
              <td className={cx(td, 'num')}>{fmtUsd(f.invested)}</td>
              <td className={cx(td, 'num')}>{fmtUsd(f.equity)}</td>
              <td className={cx(td, 'num', f.pnl.startsWith('-') ? 'text-down' : 'text-up')}>{fmtUsdSigned(f.pnl)}</td>
              <td className={cx(td, 'num')}>×{f.multiplier}</td>
              <td className={cx(td, 'num')}>
                {pct(D.from(f.stopLossPct).mul(100), 0)} · {fmtUsd(f.stopLossLevel)}
              </td>
              <td className={cx(td, 'num')}>{f.fees ? feeText(f.fees.mgmtFeePct, f.fees.perfFeePct) : '—'}</td>
              <td className={cx(td, 'num')}>{f.openPositions}</td>
              <td className={cx(td, 'text-muted')}>{fmtDateTime(f.startedAt)}</td>
              <td className={cx(td, 'text-right')}>
                {f.status !== 'stopped' && (
                  <button type="button" className={btn} onClick={() => setEdit(f)}>
                    Manage
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <AssignModal
        open={assigning}
        master={m}
        onClose={() => setAssigning(false)}
        onDone={(msg) => {
          setAssigning(false);
          setNote(msg);
          refresh();
        }}
      />
      {edit && (
        <ManageFollowerModal
          master={m}
          f={edit}
          onClose={() => setEdit(null)}
          onDone={() => {
            setEdit(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

interface UserRow {
  id: string;
  email: string;
  name?: string | null;
  externalClientId?: string | null;
  liveBalance?: string;
  /** What can move out of the live account now: the cash, less what backs open trades. */
  liveWithdrawable?: string | null;
}
const userLabel = (u: UserRow) => clientLabel({ name: u.name, email: u.email, crmId: u.externalClientId });

/** Search a client by name, email, CRM id or user id and pick one. */
function UserPicker({ value, onPick }: { value: UserRow | null; onPick: (u: UserRow | null) => void }) {
  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const { data } = useLoad<UserRow[]>(term ? `/api/admin/users?q=${encodeURIComponent(term)}&limit=10` : null, [term]);
  if (value)
    return (
      <div className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-[13px]">
        <span title={value.email}>
          {userLabel(value)}
          {value.liveBalance ? <span className="num ml-2 text-xs text-muted">live {fmtUsd(value.liveBalance)}</span> : null}
        </span>
        <button type="button" className="text-xs text-muted" onClick={() => onPick(null)}>
          Change
        </button>
      </div>
    );
  return (
    <div className="flex flex-col gap-2">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setTerm(query.trim());
        }}
      >
        <input className={cx(input, 'flex-1')} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Client name, email, CRM ID or user ID" aria-label="Search client" />
        <button type="submit" className={btn}>
          Search
        </button>
      </form>
      {data && !data.length && <p className="m-0 text-xs text-muted">No client found.</p>}
      {!!data?.length && (
        <div className="flex max-h-48 flex-col overflow-y-auto rounded-lg border border-line">
          {data.map((u) => (
            <button key={u.id} type="button" onClick={() => onPick(u)} className="flex justify-between border-b border-line px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-raised">
              <ClientCell name={u.name} email={u.email} crmId={u.externalClientId} />
              {u.liveBalance && <span className="num text-xs text-muted">live {fmtUsd(u.liveBalance)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AssignModal({ open, master: m, onClose, onDone }: { open: boolean; master: MasterSummary; onClose: () => void; onDone: (msg: string) => void }) {
  const [user, setUser] = useState<UserRow | null>(null);
  const [amount, setAmount] = useState('');
  const [multiplier, setMultiplier] = useState('1');
  const [stop, setStop] = useState(toPctText(MASTER_LIMITS.defaultStopLossPct));
  const [copyOpen, setCopyOpen] = useState(true);
  const [mgmtFee, setMgmtFee] = useState(toPctText(m.mgmtFeePct));
  const [perfFee, setPerfFee] = useState(toPctText(m.perfFeePct));
  const a = useAction();
  // The amount starts as the client's whole live balance: all of it that can move now.
  const pick = (u: UserRow | null) => {
    setUser(u);
    const all = D.from(u?.liveWithdrawable ?? '0').round(2, 'down');
    setAmount(all.isPos() ? all.toString() : '');
  };
  const submit = () =>
    a.run(async () => {
      const r = await api.post<{ opened: number; skipped: { symbol: string; reason: string }[] }>(`/api/admin/masters/${m.id}/followers`, {
        userId: user!.id,
        amount,
        multiplier,
        stopLossPct: D.from(stop || '0').div(100).toString(),
        copyOpenTrades: copyOpen,
        mgmtFeePct: toFrac(mgmtFee),
        perfFeePct: toFrac(perfFee),
      });
      const who = userLabel(user!);
      setUser(null);
      setAmount('');
      onDone(
        `${who} assigned with ${fmtUsd(amount)}. ${r.opened} open trade${r.opened === 1 ? '' : 's'} copied${r.skipped.length ? `, ${r.skipped.length} skipped (${r.skipped.map((s) => `${s.symbol}: ${COPY_SKIP_REASONS[s.reason] ?? s.reason}`).join(', ')})` : ''}.`,
      );
    });
  return (
    <Modal open={open} onClose={onClose} title={`Assign a client to ${m.name}`} width={480}>
      <UserPicker value={user} onPick={pick} />
      <div className="grid grid-cols-3 gap-3">
        <Field label={`Amount (min ${COPY_LIMITS.minAmount})`}>
          <span className="relative flex items-center">
            <span className="pointer-events-none absolute left-3 text-[13px] text-muted">$</span>
            <input className={cx(input, 'num w-full pl-7')} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </span>
        </Field>
        <Field label="Multiplier (0.1–5)">
          <input className={input} inputMode="decimal" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} />
        </Field>
        <Field label="Stop loss (% of amount)">
          <input className={input} inputMode="decimal" value={stop} onChange={(e) => setStop(e.target.value)} />
        </Field>
      </div>
      <FeeFields mgmt={mgmtFee} perf={perfFee} onMgmt={setMgmtFee} onPerf={setPerfFee} hint="The master's defaults; change them for this client if agreed otherwise." />
      <label className="flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={copyOpen} onChange={(e) => setCopyOpen(e.target.checked)} />
        Also open the master's {m.openPositions} open position{m.openPositions === 1 ? '' : 's'} now, at today's price
      </label>
      <p className="m-0 text-xs text-muted">
        The amount moves from the client's live account into a separate account for {m.name}. Trades are sized to that account's equity ÷ the master's equity × the multiplier. The client
        must have an accepted mandate.
      </p>
      <Err msg={a.error} />
      <button type="button" className={primary} disabled={a.busy || !user || !amount} onClick={() => void submit()}>
        {a.busy ? 'Assigning…' : 'Assign'}
      </button>
    </Modal>
  );
}

function ManageFollowerModal({ master: m, f, onClose, onDone }: { master: MasterSummary; f: Follower; onClose: () => void; onDone: () => void }) {
  const [multiplier, setMultiplier] = useState(f.multiplier);
  const [stop, setStop] = useState(D.from(f.stopLossPct).mul(100).toString());
  const [funds, setFunds] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [mgmtFee, setMgmtFee] = useState(toPctText(f.fees?.mgmtFeePct ?? '0'));
  const [perfFee, setPerfFee] = useState(toPctText(f.fees?.perfFeePct ?? '0'));
  const a = useAction();
  const base = `/api/admin/masters/${m.id}/followers/${f.id}`;
  const statement = useLoad<MasterFeeStatement>(`${base}/fees`);
  const act = (fn: () => Promise<unknown>, done?: string) =>
    a.run(fn).then((ok) => {
      if (ok) {
        if (done) setNote(done);
        onDone();
      }
    });
  return (
    <Modal open onClose={onClose} title={clientLabel({ name: f.user.name, email: f.user.email, crmId: f.user.externalClientId })} width={640}>
      <p className="m-0 text-xs text-muted">{f.user.email}</p>
      <p className="m-0 num text-xs text-muted">
        Invested {fmtUsd(f.invested)} · equity {fmtUsd(f.equity)} · P&L {fmtUsdSigned(f.pnl)} · {f.openPositions} open
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Multiplier">
          <input className={cx(input, 'w-24')} inputMode="decimal" value={multiplier} onChange={(e) => setMultiplier(e.target.value)} />
        </Field>
        <Field label="Stop loss %">
          <input className={cx(input, 'w-24')} inputMode="decimal" value={stop} onChange={(e) => setStop(e.target.value)} />
        </Field>
        <button
          type="button"
          className={btn}
          disabled={a.busy}
          onClick={() => void act(() => api.patch(base, { multiplier, stopLossPct: D.from(stop || '0').div(100).toString() }), 'Saved. Applies to the next trades.')}
        >
          Save
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <FeeFields mgmt={mgmtFee} perf={perfFee} onMgmt={setMgmtFee} onPerf={setPerfFee} />
        <button type="button" className={btn} disabled={a.busy} onClick={() => void act(() => api.patch(base, { mgmtFeePct: toFrac(mgmtFee), perfFeePct: toFrac(perfFee) }), 'Fees saved. They apply from today.')}>
          Save fees
        </button>
      </div>
      {statement.data && <FeeStatementView s={statement.data} />}
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Funds (+ add from live, − return to live)">
          <input className={cx(input, 'w-40')} value={funds} onChange={(e) => setFunds(e.target.value)} placeholder="e.g. 500 or -200" />
        </Field>
        <button type="button" className={btn} disabled={a.busy || !funds} onClick={() => void act(() => api.post(`${base}/funds`, { amount: funds }), 'Funds moved.')}>
          Move funds
        </button>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-line pt-3">
        {f.status === 'active' ? (
          <button type="button" className={btn} disabled={a.busy} onClick={() => void act(() => api.patch(base, { status: 'paused' }), 'Paused: new trades are skipped for this client.')}>
            Pause
          </button>
        ) : (
          <button type="button" className={btn} disabled={a.busy} onClick={() => void act(() => api.patch(base, { status: 'active' }), 'Resumed.')}>
            Resume
          </button>
        )}
        <button
          type="button"
          className={btn}
          disabled={a.busy || f.status !== 'active'}
          title="Open any master position this client is missing, at today's price"
          onClick={() => void act(() => api.post(`${base}/resync`), 'Re-synced with the master.')}
        >
          Resync
        </button>
        {!confirmUnassign ? (
          <button type="button" className={cx(btn, 'border-down text-down')} onClick={() => setConfirmUnassign(true)}>
            Unassign
          </button>
        ) : (
          <button type="button" className={cx(btn, 'bg-down text-white')} disabled={a.busy} onClick={() => void act(() => api.post(`${base}/unassign`)).then(onClose)}>
            Confirm: close positions and return cash
          </button>
        )}
      </div>
      <Err msg={a.error} />
      {note && <p className="m-0 text-xs text-up">{note}</p>}
    </Modal>
  );
}

// ---------- mandates ----------

function MandatesSection({ master: m }: { master: MasterSummary }) {
  const { data, error, reload } = useLoad<{ mandates: Mandate[] }>(`/api/admin/masters/${m.id}/mandates`);
  const [user, setUser] = useState<UserRow | null>(null);
  const [signed, setSigned] = useState(false);
  const [note, setNote] = useState('');
  const [revoking, setRevoking] = useState<MandateSummary | null>(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [reason, setReason] = useState('');
  const a = useAction();
  const mandates = data?.mandates ?? [];

  const create = () =>
    a.run(async () => {
      await api.post(`/api/admin/masters/${m.id}/mandates`, { userId: user!.id, signed, note });
      setUser(null);
      setNote('');
      setSigned(false);
      setPickerKey((k) => k + 1);
      await reload();
    });
  const revoke = () =>
    a.run(async () => {
      await api.post(`/api/admin/mandates/${revoking!.id}/revoke`, { reason });
      setRevoking(null);
      setReason('');
      await reload();
    });

  return (
    <div className="flex flex-col gap-3">
      {m.status !== 'closed' && (
        <div className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-3">
          <h3 className="m-0 text-sm font-semibold">Add a mandate</h3>
          <UserPicker key={pickerKey} value={user} onPick={setUser} />
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} />
            The client signed a mandate outside the app (counts as accepted now)
          </label>
          {signed && (
            <Field label="Where the signed copy is kept (required)">
              <input className={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. CRM ticket 881" />
            </Field>
          )}
          <p className="m-0 text-xs text-muted">{signed ? 'Recorded as accepted by you.' : 'The client gets a notification and accepts the mandate in the app.'}</p>
          <Err msg={a.error} />
          <button type="button" className={cx(primary, 'self-start')} disabled={a.busy || !user || (signed && !note.trim())} onClick={() => void create()}>
            {signed ? 'Record signed mandate' : 'Offer mandate'}
          </button>
        </div>
      )}
      <Err msg={error} />
      {!data && !error && <Spinner />}
      {data && !mandates.length && <p className="m-0 text-sm text-muted">No mandates yet.</p>}
      {!!mandates.length && (
        <Table head={['Client', 'Status', 'Accepted by', 'Accepted', 'Version', 'Created', '']} minWidth={700}>
          {mandates.map((x) => (
            <tr key={x.id} className="border-b border-line">
              <td className={td}>
                <ClientCell name={x.userName} email={x.userEmail} crmId={x.userExternalClientId} />
              </td>
              <td className={td}>
                <span className={cx('rounded-full px-2 py-0.5 text-[11px] font-semibold', x.status === 'accepted' ? 'bg-up/15 text-up' : x.status === 'offered' ? 'bg-warn/15 text-warn' : 'bg-raised text-muted')}>{x.status}</span>
              </td>
              <td className={td}>{x.acceptedBy ?? '—'}</td>
              <td className={cx(td, 'text-muted')}>{fmtDateTime(x.acceptedAt)}</td>
              <td className={td}>{x.version}</td>
              <td className={cx(td, 'text-muted')}>{fmtDateTime(x.createdAt)}</td>
              <td className={cx(td, 'text-right')}>
                {x.status !== 'revoked' && (
                  <button type="button" className={btn} onClick={() => setRevoking(x)}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!revoking} onClose={() => setRevoking(null)} title="Revoke mandate?" width={420}>
        <p className="m-0 text-[13px]">If the client follows {m.name}, they are unassigned: positions close and the cash goes back to their live account.</p>
        <Field label="Reason (kept in the audit and sent to the CRM)">
          <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <Err msg={a.error} />
        <button type="button" className={cx(primary, 'bg-down')} disabled={a.busy || !reason.trim()} onClick={() => void revoke()}>
          Revoke
        </button>
      </Modal>
    </div>
  );
}

// ---------- settings ----------

function SettingsSection({ master: m, onChanged }: { master: MasterSummary; onChanged: () => void }) {
  const [name, setName] = useState(m.name);
  const [lev, setLev] = useState(m.maxLeverage ? String(m.maxLeverage) : '');
  const [maxOrder, setMaxOrder] = useState(m.maxOrder ?? '');
  const [instruments, setInstruments] = useState(m.instruments.join(', '));
  const [note, setNote] = useState(m.note);
  const [mgmtFee, setMgmtFee] = useState(toPctText(m.mgmtFeePct));
  const [perfFee, setPerfFee] = useState(toPctText(m.perfFeePct));
  const [saved, setSaved] = useState(false);
  const a = useAction();
  const closed = m.status === 'closed';
  const save = () =>
    a.run(async () => {
      setSaved(false);
      await api.patch(`/api/admin/masters/${m.id}`, {
        name,
        maxLeverage: lev || null,
        maxOrder: maxOrder || null,
        instruments: instruments
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        note,
        mgmtFeePct: toFrac(mgmtFee),
        perfFeePct: toFrac(perfFee),
      });
      setSaved(true);
      onChanged();
    });
  return (
    <div className="flex max-w-xl flex-col gap-3">
      <Field label="Name (clients see it)">
        <input className={input} value={name} disabled={closed} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Max leverage (empty = instrument max)">
          <input className={input} inputMode="numeric" value={lev} disabled={closed} onChange={(e) => setLev(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="Instruments (empty = all)">
          <input className={input} value={instruments} disabled={closed} onChange={(e) => setInstruments(e.target.value.toUpperCase())} />
        </Field>
      </div>
      <MaxOrderField value={maxOrder} disabled={closed} onChange={setMaxOrder} />
      <FeeFields mgmt={mgmtFee} perf={perfFee} onMgmt={setMgmtFee} onPerf={setPerfFee} hint="Defaults for clients assigned from now on. Running followers keep their own terms (change them under Followers → Manage)." />
      <Field label="Note (internal)">
        <textarea className={cx(input, 'h-24 py-2')} value={note} disabled={closed} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <p className="m-0 text-xs text-muted">
        Reference equity {fmtUsd(m.referenceEquity)} (fixed). Account {m.accountId}.
      </p>
      <Err msg={a.error} />
      {saved && <p className="m-0 text-xs text-up">Saved.</p>}
      {!closed && (
        <button type="button" className={cx(primary, 'self-start')} disabled={a.busy} onClick={() => void save()}>
          Save
        </button>
      )}
    </div>
  );
}

/** The master's own max order (MASTER_ORDER_SIZE_PLAN.md): the instruments' client max order does not apply to it. */
function MaxOrderField({ value, disabled, onChange }: { value: string; disabled?: boolean; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <Field label="Max order, USDT (optional)">
        <input className={input} inputMode="decimal" value={value} disabled={disabled} placeholder="No limit beyond its margin" onChange={(e) => /^\d*\.?\d{0,2}$/.test(e.target.value) && onChange(e.target.value)} />
      </Field>
      <p className="m-0 text-xs text-muted">
        The largest single order on this master. Empty: no limit beyond its margin. The instruments’ client max order does not apply to the master; each follower’s copy still stays within it.
      </p>
    </div>
  );
}

// ---------- fees ----------

function FeeFields({ mgmt, perf, onMgmt, onPerf, hint }: { mgmt: string; perf: string; onMgmt: (v: string) => void; onPerf: (v: string) => void; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-3">
        <Field label="Management fee (% a year, max 5)">
          <input className={cx(input, 'w-28')} inputMode="decimal" value={mgmt} onChange={(e) => onMgmt(e.target.value)} />
        </Field>
        <Field label="Performance fee (% of gains above the high-water mark, max 50)">
          <input className={cx(input, 'w-28')} inputMode="decimal" value={perf} onChange={(e) => onPerf(e.target.value)} />
        </Field>
      </div>
      {hint && <p className="m-0 text-[11px] text-muted">{hint}</p>}
    </div>
  );
}

/** A follower's fee terms and settlements (also what the client sees as their statement). */
export function FeeStatementView({ s }: { s: MasterFeeStatement }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 num text-xs text-muted">
        High-water mark {fmtUsd(s.highWaterMark)} · management fee accrued, not yet charged {fmtUsd(s.accrued)}
      </p>
      {!s.charges.length ? (
        <p className="m-0 text-xs text-muted">No settlements yet. Fees are settled on the 1st of each month and when the account closes.</p>
      ) : (
        <Table head={['Period', 'Equity', 'Management', 'Performance', 'High-water mark', 'Charged']} minWidth={560}>
          {s.charges.map((c) => (
            <tr key={c.period} className="border-b border-line">
              <td className={cx(td, 'font-semibold')}>{c.period === 'final' ? 'Final' : c.period}</td>
              <td className={cx(td, 'num')}>{fmtUsd(c.equity)}</td>
              <td className={cx(td, 'num')}>
                {fmtUsd(c.management)}
                {D.from(c.unpaid).isPos() ? <span className="text-warn"> ({fmtUsd(c.unpaid)} carried)</span> : null}
              </td>
              <td className={cx(td, 'num')}>{fmtUsd(c.performance)}</td>
              <td className={cx(td, 'num')}>
                {fmtUsd(c.hwmBefore)} → {fmtUsd(c.hwmAfter)}
              </td>
              <td className={cx(td, 'text-muted')}>{fmtDateTime(c.chargedAt)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}
